import React, { useEffect, useState } from 'react';
import { Copy, Loader2, Save, TestTube2 } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import {
  CALLBACK_PATH, PRIMEVPAY_DEFAULTS, changedFields,
} from '../lib/primevpay';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

/**
 * Ayarlar → Finans → PrimeVpay.
 *
 * Bölüm kendi state'ini ve Kaydet düğmesini taşır: ayarlar
 * `provider_configs` satırındadır ve `PATCH /admin/settings` gövdesine hiç
 * girmez. `onEnabledChange` finans bölüm seçicisindeki aktiflik noktasını
 * besler.
 */
const PrimevpaySettings = ({ paymentBase = '', siteBase = '', onEnabledChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(PRIMEVPAY_DEFAULTS);
  const [initial, setInitial] = useState(PRIMEVPAY_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  // Ayarlar okunamadıysa form varsayılanları çizer; kaydetmek onları canlının
  // üzerine yazmak olurdu, bu yüzden bölüm okunana kadar salt okunurdur.
  const [loadError, setLoadError] = useState('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const callbackBase = paymentBase || origin;
  const returnBase = siteBase || origin;
  const callbackUrl = `${callbackBase}${CALLBACK_PATH}`;

  const apply = (data) => {
    const merged = { ...PRIMEVPAY_DEFAULTS, ...data, api_secret: '' };
    setForm(merged);
    setInitial(merged);
    setMeta(data || {});
    onEnabledChange?.(Boolean(data?.enabled));
  };

  useEffect(() => {
    let alive = true;
    client.get('/admin/primevpay/config')
      .then(({ data }) => {
        if (!alive) return;
        apply(data);
        setLoadError('');
      })
      .catch((error) => {
        if (!alive) return;
        const message = errorText(error, 'Bilinmeyen hata');
        setLoadError(message);
        toast({
          title: 'PrimeVpay ayarları okunamadı',
          description: message,
          variant: 'destructive',
        });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const pending = changedFields(form, initial, {
    paymentBase: callbackBase, siteBase: returnBase,
  });
  const dirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (loadError || !dirty) return;
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/primevpay/config', pending);
      apply(data);
      toast({ title: 'PrimeVpay ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'PrimeVpay kaydedilemedi',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const { data } = await client.post('/admin/primevpay/test');
      toast({ title: data?.message || 'PrimeVpay bağlantısı başarılı' });
    } catch (error) {
      toast({
        title: 'PrimeVpay bağlantı testi başarısız',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast({ title: 'Kopyalandı' }))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> PrimeVpay ayarları yükleniyor…
      </div>
    );
  }

  return (
    <>
      <div className="col-span-full mt-1">
        <h3 className="text-sm font-bold text-white">PrimeVpay</h3>
        <p className="mt-1 text-xs text-slate-400">
          Havale/EFT yatırım ve IBAN'a çekim. Oyuncu yatırımda PrimeVpay'in
          <strong> ödeme sayfasına</strong> yönlendirilir; atanan IBAN ve
          &quot;ödemeyi yaptım&quot; adımı oradadır. Çekim önce
          <strong> yönetici onayından</strong> geçer, sonra sağlayıcıya iletilir.
          Sonuç imzalı bildirimle gelir; bildirimi kaçan kayıtlar arka planda
          sorulur. Ayarlar kaydedilince oyuncunun yöntem listesindeki
          <strong> PrimeVpay Havale / EFT</strong> satırı da güncellenir.
        </p>
      </div>

      {loadError && (
        <div className="col-span-full rounded-lg border border-rose-500/40 p-3 text-xs text-rose-200">
          Ayarlar okunamadı ({loadError}); bölüm salt okunur.
        </div>
      )}

      <div className="col-span-full provider-enable">
        <label className="flex items-center gap-2 text-sm font-semibold text-white">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
          />
          PrimeVpay entegrasyonu aktif
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Açmak için API anahtarı, secret ve bildirim adresinin PrimeVpay
          panelinde tanımlandığı onayı gerekir.
        </p>
      </div>

      <label className="settings-inline-field">
        API adresi
        <input
          value={form.api_url || ''}
          onChange={(e) => set('api_url', e.target.value)}
          className="admin-input mt-1"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Ödeme alan adıdır; panele girdiğiniz adres API için kullanılmaz.
        </span>
      </label>
      <label className="settings-inline-field">
        API anahtarı (x-api-key)
        <input
          value={form.api_key || ''}
          onChange={(e) => set('api_key', e.target.value)}
          className="admin-input mt-1"
          autoComplete="off"
        />
      </label>
      <label className="settings-inline-field">
        API secret
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.api_secret_configured ? 'Kayıtlı — değiştirmek için yazın' : 'PrimeVpay api_secret'}
          value={form.api_secret}
          onChange={(e) => set('api_secret', e.target.value)}
          className="admin-input mt-1"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Her isteği ve gelen bildirimi imzalar (HMAC-SHA256); sunucuya
          kaydedilir, geri gösterilmez.
          {meta.previous_identity_active && (
            <> Önceki secret anahtar değişiminden sonraki 8 saat boyunca
            yalnızca gelen bildirimlerde kabul ediliyor.</>
          )}
        </span>
      </label>

      <div className="col-span-full rounded-lg border border-amber-500/40 p-3">
        <div className="text-sm font-semibold text-white">Test modu</div>
        <p className="mt-1 text-[11px] leading-4 text-slate-400">
          Mod buradan seçilmez, PrimeVpay'in cevabından okunur. PrimeVpay yeni
          siteyi Test modunda açar: işlemler gerçek ödeme akışına girmez ve
          sonucu PrimeVpay site panelindeki <strong>Test İşlemleri</strong>
          ekranından siz verirsiniz. Test modunda açılan işlemin onayı
          <strong> bakiyeye yansır</strong> — denemeleri yalnızca deneme
          hesabıyla yapın. Site Canlı'ya alındıktan sonra açılan işlemlere test
          işaretli bir sonuç gelirse yok sayılır.
        </p>
      </div>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="text-xs text-slate-300">
          Webhook adresi (yatırım ve çekim için aynı)
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200">
            {callbackUrl}
          </code>
          <button type="button" onClick={() => copy(callbackUrl)} className="admin-btn-secondary p-2" aria-label="Kopyala">
            <Copy size={13} />
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Adres <strong>istekte gönderilmez</strong>; PrimeVpay site panelinde
          <strong> API &amp; Webhook</strong> ekranına girilir. Girdikten sonra
          oradaki <strong>Test Gönder</strong> düğmesiyle deneyin. API
          anahtarı ya da bu adres değiştiğinde onay kendiliğinden kalkar.
        </p>
        <label className="mt-2 flex items-center gap-2 text-xs text-white">
          <input
            type="checkbox"
            checked={!!form.callback_registered}
            onChange={(e) => set('callback_registered', e.target.checked)}
          />
          Webhook adresini PrimeVpay panelinde tanımladım
        </label>
      </div>

      {[
        { key: 'deposit', title: 'yatırım', cls: 'is-deposit' },
        { key: 'withdraw', title: 'çekim', cls: 'is-withdraw' },
      ].map((side) => (
        <section key={side.key} className={`provider-money ${side.cls}`}>
          <label className="flex items-center gap-2 text-sm font-bold text-white">
            <input
              type="checkbox"
              checked={form[`${side.key}_enabled`] !== false}
              onChange={(e) => set(`${side.key}_enabled`, e.target.checked)}
            />
            PrimeVpay {side.title} açık
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['min', 'max'].map((bound) => (
              <label key={bound} className="settings-inline-field">
                {bound === 'min' ? 'Minimum' : 'Maksimum'} {side.title} (₺)
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form[`${side.key}_${bound}_amount`] ?? ''}
                  onChange={(e) => set(`${side.key}_${bound}_amount`, Number(e.target.value))}
                  className="admin-input mt-1"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="col-span-full flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!!busy || !dirty || !!loadError}
          className="admin-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          PrimeVpay Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={test}
          disabled={!!busy || !meta.configured}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Bağlantıyı Test Et
        </button>
      </div>
      <p className="col-span-full text-[11px] leading-4 text-slate-400">
        Bağlantı testi var olmayan bir işlemin durumunu sorar (işlem üretmez):
        &quot;bulunamadı&quot; cevabı anahtarın ve imzanın kabul edildiğini
        söyler; anahtar ya da secret hatalıysa sağlayıcının cevabı olduğu gibi
        gösterilir.
      </p>
    </>
  );
};

export default PrimevpaySettings;
